import type { Options } from '@nuxtjs/i18n';
import { PageTypes, getPagePaths } from './types/PageTypes';
import { authorizedLanguages, countries, type CTCountry } from './utils/localization';

export type CommercetoolsI18nOptions = Options & {
  currency: string;
  country: string;
  countries: CTCountry[];
  currencies: any[];
  autoRedirectByLocale: boolean;
  autoChangeCookie: {
    currency: boolean;
    country: boolean;
  };
};

export const acceptLanguage = authorizedLanguages.map((locale) => locale.code);
export const localesPathPrefix = authorizedLanguages.map((locale) => locale.pathPrefix);

// Prefix for slugs that aren't supposed to be exposed (but must be published because fr is default locale in contentful)
export const DUMBSLUG_PREFIX = 'dumbslug';

export function isAuthorizedLanguage(lang: string): boolean {
  return authorizedLanguages.some((l) => l.code === lang);
}

// Check if seo is activated for a locale. used for hreflangs and sitemap
export function isSeoActivatedForLanguage(lang: string): boolean {
  const langDef = authorizedLanguages.find((l) => l.code === lang);
  return langDef?.seo;
}

export function getVATNumberForCountry(country: string): string {
  switch (country) {
    case 'CH':
      return 'CHE-389.003.094';
    case 'GB':
      return '371 1091 26 - EORI: GB075001057000';
    default:
      return 'FR 38 334 806 502';
  }
}

export const i18n: CommercetoolsI18nOptions = {
  strategy: 'prefix_except_default',
  defaultLocale: 'fr',
  langDir: 'lang/',
  lazy: true,
  parsePages: false,
  detectBrowserLanguage: false,
  pages: {
    'legacy/ThankYou': getPagePaths(acceptLanguage, PageTypes.THX),
    'legacy/ResetPassword': getPagePaths(acceptLanguage, PageTypes.RESET_PASSWORD),
    'legacy/Basic/_slug': getPagePaths(acceptLanguage, PageTypes.BASIC),
    'legacy/GenericCategory/GenericCategory': getPagePaths(acceptLanguage, PageTypes.CATEGORY),
    'legacy/Recipe/_slug': getPagePaths(acceptLanguage, PageTypes.RDP),
    'product/_slug': getPagePaths(acceptLanguage, PageTypes.PDP),
    'guides': getPagePaths(acceptLanguage, PageTypes.EOLP),
    'legacy/FAQ/Index': getPagePaths(acceptLanguage, PageTypes.KNOWLEDGE),
    'legacy/FAQ/ContactForm': getPagePaths(acceptLanguage, PageTypes.CONTACT),
    'legacy/AllProducts': getPagePaths(acceptLanguage, PageTypes.ALL),
    'legacy/Survey/Survey': getPagePaths(acceptLanguage, PageTypes.SUR),
    'legacy/Survey/SurveyResult': getPagePaths(acceptLanguage, PageTypes.SRE),
    'legacy/Survey/_slug': getPagePaths(acceptLanguage, PageTypes.GSUR),
    'legacy/SurveyResult/_slug': getPagePaths(acceptLanguage, PageTypes.GSRE),
    'legacy/Invoice/_id': getPagePaths(acceptLanguage, PageTypes.INVOICE),
    'legacy/AdyenRedirectBack': getPagePaths(acceptLanguage, PageTypes.ADYEN_REDIRECT),
    'all-advices': getPagePaths(acceptLanguage, PageTypes.CUSTOM_ALL_ADVICES),
  },
  // NOTE: Order matters for $acceptLanguage parameters in Commercetools:
  // "List of languages the client is able to understand, and which locale variant is preferred."
  locales: authorizedLanguages,
  vueI18n: {
    fallbackLocale: 'fr',
    numberFormats: {
      fr: {
        currency: {
          style: 'currency',
          currency: 'EUR',
          currencyDisplay: 'symbol',
        },
      },
      it: {
        currency: {
          style: 'currency',
          currency: 'EUR',
          currencyDisplay: 'symbol',
        },
      },
    },
    dateTimeFormats: {
      fr: {
        day: {
          day: 'numeric',
        },
        short: {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        },
        long: {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: '2-digit',
        },
      },
      it: {
        day: {
          day: 'numeric',
        },
        short: {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        },
        long: {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: '2-digit',
        },
      },
    },
  },
  autoRedirectByLocale: false,
  autoChangeCookie: {
    currency: false,
    country: false,
  },
  currency: 'EUR',
  currencies: [{ name: 'EUR', label: '€ Euro (EUR)' }],
  country: 'FR',
  countries,
};
